import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { usePaymentsStore } from "@/plugins/store";
import { routeNames } from "@/constants/routeNames";
import { PaymentMethods } from "@esenda/core-ui/constants";

const mainRoutes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: routeNames.DEFAULT,
        component: () => import("@/views/Home.vue"),
    },
    {
        path: "/:shortcode/:form/:orderId",
        name: routeNames.FORM,
        component: () => import("@/views/payment/EntryPage.vue"),
        meta: { checkShortcode: true, checkFormName: true },
    },
    {
        path: "/:catchAll(.*)*",
        name: routeNames.NOT_FOUND,
        component: () => import("@/views/default/NotFoundPage.vue"),
    },
    {
        path: "/:shortcode/:form/payment",
        name: routeNames.PAYMENT_METHOD,
        component: () =>
            import("@/views/payment/methods/PaymentMethodPage.vue"),
        meta: {
            requiresPaymentMethods: true,
            checkShortcode: true,
            checkFormName: true,
        },
    },
    /* Lloyds Card Payment temporarily disabled on 25.08.2026, see cardForm.vue
    {
        path: "/lloyds-3ds-challenge-iframe",
        name: routeNames.LLOYDS_CHALLENGE_IFRAME,
        component: () =>
            import("@/views/payment/providers/cards/LloydsChallengeIframe.vue"),
    },
    {
        path: "/lloyds-3ds-method-form-iframe",
        name: routeNames.LLOYDS_METHOD_FORM_IFRAME,
        component: () =>
            import("@/views/payment/providers/cards/LloydsMethodFormIframe.vue"),
    },
    */
    {
        path: "/:shortcode/:form/payment/authorisation-returned",
        name: routeNames.AUTHORISATION_RETURNED,
        component: () => import("@/views/payment/AuthorisationReturned.vue"),
        meta: { checkShortcode: true, checkFormName: true },
    },
    {
        path: "/:shortcode/:form/payment/status/:id",
        name: routeNames.PAYMENT_STATUS,
        component: () => import("@/views/payment/Status.vue"),
        meta: { checkShortcode: true, checkFormName: true, checkId: true },
    },
    {
        path: "/unauthorised",
        name: routeNames.ACCESS_DENIED,
        component: () => import("@/views/default/AccessDeniedPage.vue"),
    },
    {
        path: "/:shortcode/:form/payment-link-unavailable",
        name: routeNames.PAYMENT_LINK_UNAVAILABLE,
        component: () => import("@/views/default/PaymentLinkUnavailable.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: true,
        },
    },
    {
        path: "/:shortcode/tokenise/:tokenisedCardId",
        name: routeNames.TOKENISE_CARD_DETAILS_ENTRY_PAGE,
        component: () => import("@/views/tokenisation/EntryPage.vue"),
        meta: {
            checkShortcode: true,
            checkTokenisedCardId: true,
        },
    },
    {
        path: "/:shortcode/tokenise/:tokenisedCardId/enter-details",
        name: routeNames.TOKENISE_CARD_DETAILS,
        component: () => import("@/views/tokenisation/Tokenise.vue"),
        meta: {
            checkShortcode: true,
            checkTokenisedCardId: true,
        },
    },
    {
        path: "/:shortcode/tokenise/:tokenisedCardId/status",
        name: routeNames.TOKENISATION_STATUS,
        component: () => import("@/views/tokenisation/Status.vue"),
        meta: {
            checkShortcode: true,
            checkTokenisedCardId: true,
        },
    },
    {
        path: "/:shortcode/tokenise/:tokenisedCardId/unavailable",
        name: routeNames.TOKENISATION_UNAVAILABLE,
        component: () => import("@/views/tokenisation/Unavailable.vue"),
        meta: {
            checkShortcode: true,
            checkTokenisedCardId: true,
        },
    },
];

const routes = mainRoutes;

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach((to, from) => {
    const { paymentMethods } = usePaymentsStore();

    /** Gets the meta properties associated to the route */
    const requiresPaymentMethods = to.matched.some(
        (x) => x.meta.requiresPaymentMethods,
    );
    const requiresShortcodeParam = to.matched.some(
        (x) => x.meta.checkShortcode,
    );
    const requiresFormNameParam = to.matched.some((x) => x.meta.checkFormName);
    const requiresIdParam = to.matched.some((x) => x.meta.checkId);
    const requiresRedirectToNewLink = to.matched.some(
        (x) => x.meta.requiresRedirectToNewLink,
    );
    const requiresTokenisedCardIdParam = to.matched.some(
        (x) => x.meta.checkTokenisedCardId,
    );

    /**
     * Redirects to 404 if the route requiresShortcodeParam and the
     * shortcode param is null or undefined
     */
    if (requiresShortcodeParam) {
        if (["null", "undefined"].includes(String(to.params.shortcode))) {
            return "/";
        }
    }

    /**
     * Redirects to 404 if the route requiresFormNameParam and the
     * form name param is null or undefined
     */
    if (requiresFormNameParam) {
        if (["null", "undefined"].includes(String(to.params.form))) {
            return "/";
        }
    }

    /**
     * Redirects to 404 if the route requiresIdParam and the
     * id param is null or undefined
     */
    if (requiresIdParam) {
        if (["null", "undefined"].includes(String(to.params.id))) {
            return "/";
        }
    }

    /**
     * Redirects to the first page if the route requiresPaymentMethods and
     * the payment method list in the session is null
     */
    if (requiresPaymentMethods) {
        /**
         * Redirects to the first page if the payment methods list is null
         * and the user is not coming from the form page.
         */
        if (paymentMethods === null && from.name !== "Form") {
            return `/${to.params.shortcode}/${to.params.form}`;
        }

        /**
         * If the payment methods list is not null but only contains unsupported methods (Lloyds),
         * redirect to the payment link unavailable page.
         */
        if (
            paymentMethods !== null &&
            !paymentMethods.some(
                (method) => method.paymentMethod !== PaymentMethods.LLOYDS,
            )
        ) {
            return {
                name: routeNames.PAYMENT_LINK_UNAVAILABLE,
                params: {
                    shortcode: to.params.shortcode,
                    form: to.params.form,
                },
            };
        }
    }

    /**
     * Redirects to the form page if the route requiresRedirectToNewLink and
     * it adds the payment_reference param as the form name
     */
    if (requiresRedirectToNewLink) {
        return `/${to.params.shortcode}/${to.query.payment_reference}`;
    }

    /**
     * Redirects to 404 if the route requiresTokenisedCardIdParam and the
     * tokenisedCardId param is null or undefined
     */
    if (requiresTokenisedCardIdParam) {
        if (["null", "undefined"].includes(String(to.params.tokenisedCardId))) {
            return "/";
        }
    }
});

export const redirectTo404 = () => {
    router.push({ name: routeNames.NOT_FOUND });
};

export const redirectToAccessDenied = () => {
    router.push({ name: routeNames.ACCESS_DENIED });
};

export const redirectToPaymentStatus = (
    shortcode: string,
    formName: string,
    id: string,
) => {
    router.push({
        name: routeNames.PAYMENT_STATUS,
        params: {
            shortcode: shortcode,
            form: formName,
            id: id,
        },
    });
};

export const redirectToTokenisationStatus = (
    shortcode: string,
    tokenisedCardId: string,
) => {
    router.push({
        name: routeNames.TOKENISATION_STATUS,
        params: {
            shortcode: shortcode,
            tokenisedCardId: tokenisedCardId,
        },
    });
};

export const redirectToRoute = (name: string, params: any) => {
    router.push({
        name: name,
        params: params,
    });
};

export default router;
